import React, { useEffect, useState, useCallback } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import YourBusiness from './pages/YourBusiness';
import EmployeeDetails from './pages/EmployeeDetails';
import Loading from './pages/Loading';
import Error from './pages/Error';
import Header from './components/header/Header';
import Sidebar from './components/sidebar/Sidebar';
import './App.scss';
import Footer from './components/footer/Footer';
import JourneyProgress from './components/journeyProgress/JourneyProgress';
import ChooseCover from './pages/ChooseCover';
import YourQuotes from './pages/YourQuotes';
import NoQuotes from './pages/NoQuotes';
import Payment from './pages/Payment';
import AboutYou from './pages/AboutYou';
import PaymentCompleted from './pages/PaymentCompleted';
import Login from './pages/Login';
import YourAccount from './pages/YourAccount';
import ChangePassword from './pages/ChangePassword';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import IncompleteQuote from './pages/IncompleteQuote';
import MTA from './pages/MTA';
import YourDocuments from './pages/YourDocuments';
import YourRenewal from './pages/YourRenewal';
import RenewalPayment from './pages/RenewalPayment';
import Maintenance from './pages/Maintenance';
import ErrorBoundary from './ErrorBoundary/ErrorBoundary';
import './override.scss';

function App() {

    const [page, setPage] = useState(0);
    const [quotesSubHeader, setQuotesSubHeader] = useState("Instant");
    const [showSideBar, setShowSidebar] = useState(false);
    const [configLoaded, setConfigLoaded] = useState(false);
    const [config, setConfig] = useState<any>("");
    const [showMaintenancePage, setShowMaintenancePage] = useState(false);
    const navigate = useNavigate();
    var userData = JSON.parse(localStorage.getItem('userData') || 'null');

    let customerDetails: CustomerDetails = {
        "activityID": "",
        "address": {
            "house": "",
            "street": "",
            "locality": "",
            "city": "",
            "county": "",
            "country": "",
            "postcode": ""
        },
        "agentID": "",
        "businessTelephoneNumber": "",
        "campaignID": "",
        "contactName": "",
        "contactPreferences": {
            "email": true,
            "emailSpecified": true,
            "letter": true,
            "letterSpecified": true,
            "phone": true,
            "phoneSpecified": true,
            "sms": true,
            "smsSpecified": true
        },
        "dob": "",
        "email": "",
        "filingCode": "",
        "forename": "",
        "homeTelephoneNumber": "",
        "insuredPartyHID": 0,
        "insuredPartyHIDSpecified": false,
        "insuredPartyID": "",
        "ipAddress": "",
        "isCompany": false,
        "isCompanySpecified": false,
        "isConverted": false,
        "isConvertedSpecified": false,
        "loginErrorMessage": "",
        "marketingReference": "",
        "mobile": "",
        "mothersMaidenName": "",
        "password": "",
        "policyDetailsHID": 0,
        "policyDetailsHIDSpecified": false,
        "policyDetailsID": "",
        "policyNumber": "",
        "policyStartDate": new Date().toISOString(),
        "policyStartDateSpecified": false,
        "policyStatus": "",
        "prefferedMethodContact": "",
        "productID": "",
        "productType": 0,
        "productTypeSpecified": false,
        "sourceOfBusinessID": "",
        "stopAtRenewal": false,
        "stopAtRenewalSpecified": false,
        "surname": "",
        "title": "",
        "webReference": "",
        "websiteAddress": ""
    };

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    };

    useEffect(() => {
        scrollToTop();
    }, [page])

    const setCurrentPage = (pageNumber: number) => {
        setPage(pageNumber);
        if (pageNumber !== 4) {
            setQuotesSubHeader("Instant");
        }
    }

    const setQuotesStepperSubHeader = (quotesCount: number) => {
        let subHeader = `${quotesCount.toString()} quotes`;
        setQuotesSubHeader(subHeader);
    }

    const setSidebarVisibility = (visible: boolean) => {
        if (config.sidebarEnablement) {
            setShowSidebar(visible);
        }
    }

    //useCallback to retain orginal EventListner rendered in function
    const loginSuccess = useCallback((userDataString: string) => {
        window.addEventListener("click", resetInactivityTimer);
        window.addEventListener("keydown", resetInactivityTimer);
        localStorage.setItem('userData', userDataString);
        userData = JSON.parse(userDataString);
        navigate('/YourAccount');
    }, []);

    //useCallback to remove original Eventlistner rendered in function
    const logout = useCallback(() => {
        window.removeEventListener("click", resetInactivityTimer);
        window.removeEventListener("keydown", resetInactivityTimer);
        localStorage.removeItem('userData');
        userData = null;
        navigate('/Login')
    }, []);

    const resetInactivityTimer = () => {
        if (!userData) {
            return;
        }
        userData.inactivityExpiry = Date.now() + 10 * 60 * 1000;
        localStorage.setItem("userData", JSON.stringify(userData));
    };

    const checkUserTokenExpiration = () => {
        if (!userData) {
            return;
        }
        const currentTime = Date.now();
        if (userData.tokenExpiry && currentTime >= userData.tokenExpiry) {
            logout();
            return;
        }
        if (userData.inactivityExpiry && currentTime >= userData.inactivityExpiry) {
            logout();
            return;
        }
    };

    //Mount and Unmount EventListner if page refreshed
    useEffect(() => {
        fetch("/config.json")
            .then(response => {
                return response.json();
            })
            .then(data => {
                if (data.activeSite !== "") {
                    let activeSite = data.activeSite
                    document.title = data[activeSite].browserTitle;
                    setShowMaintenancePage(data.maintenanceMode)
                    localStorage.setItem("siteConfig", JSON.stringify({
                        ...data[activeSite],
                        productDetailsEnabledProducts: data?.productDetailsEnabledProducts
                    }));
                    setConfig(data[activeSite]);
                }
            })

        const interval = setInterval(checkUserTokenExpiration, 1000);
        if (userData) {
            window.addEventListener("click", resetInactivityTimer);
            window.addEventListener("keydown", resetInactivityTimer);
        }
        return () => {
            clearInterval(interval);
            window.removeEventListener("click", resetInactivityTimer);
            window.removeEventListener("keydown", resetInactivityTimer);
        }
    }, [])

    useEffect(() => {
        if (config != "" && localStorage.getItem("siteConfig") != null) {
            setConfigLoaded(true);
            document.body.classList.add(config.activeSite);
        } else {
            setConfigLoaded(false);
        }
    }, [config])

    if (showMaintenancePage) {
        return (
            <>
                <Header isSidebarVisible={showSideBar} />
                <Maintenance />
                <Footer />
            </>
        )
    }

    return (
        <>
            {configLoaded && <>
                <Header isSidebarVisible={showSideBar} />
                {page > 0 && page < 5 &&
                    <JourneyProgress page={page} quotesSubHeader={quotesSubHeader} />
                }
                {(page === 0 && showSideBar || page === 5) &&
                    <div className="header-gap"></div>
                }
                <Routes>
                    <Route path="/" element={<Login
                        setCurrentPage={setCurrentPage}
                        setSidebarVisibility={setSidebarVisibility}
                        loginSuccess={loginSuccess}
                    />} />
                </Routes>
                <ErrorBoundary fallback={<Error
                    errorTitle="Error"
                    errorMessage="Unknown error occured while processing your request."
                    setCurrentPage={setCurrentPage}
                    setSidebarVisibility={setSidebarVisibility} />}>
                    <div className="container">
                        <span className="main-content">
                            <Routes>
                                <Route path="/AboutYou" element={<AboutYou setCurrentPage={setCurrentPage} setSidebarVisibility={setSidebarVisibility} />} />
                                <Route path="/YourBusiness" element={<YourBusiness setCurrentPage={setCurrentPage} setSidebarVisibility={setSidebarVisibility} />} />
                                <Route path="/EmployeeDetails" element={<EmployeeDetails setCurrentPage={setCurrentPage} setSidebarVisibility={setSidebarVisibility} />} />
                                <Route path="/ChooseCover" element={<ChooseCover setCurrentPage={setCurrentPage} setSidebarVisibility={setSidebarVisibility} />} />
                                <Route path="/YourQuotes" element={<YourQuotes setCurrentPage={setCurrentPage} setQuotesStepperSubHeader={setQuotesStepperSubHeader} setSidebarVisibility={setSidebarVisibility} />} />
                                <Route path="/NoQuotes" element={<NoQuotes setCurrentPage={setCurrentPage} setQuotesStepperSubHeader={setQuotesStepperSubHeader} setSidebarVisibility={setSidebarVisibility} />} />
                                <Route path="/Payment" element={<Payment setCurrentPage={setCurrentPage} setSidebarVisibility={setSidebarVisibility} />} />
                                <Route path="/Loading"
                                    element={<Loading
                                        pageToLoad="/Loading"
                                        loadingTitle="Loading"
                                        loadingMessage="Loading content..."
                                        trade={null}
                                        status={null}
                                        customerDetails={customerDetails}
                                        setCurrentPage={setCurrentPage}
                                        setSidebarVisibility={setSidebarVisibility}
                                    />}
                                />
                                <Route path="/Error" element={<Error
                                    errorTitle="Error"
                                    errorMessage="Unknown error occured while processing your request."
                                    setCurrentPage={setCurrentPage}
                                    setSidebarVisibility={setSidebarVisibility} />} />

                                <Route path="/Completed" element={<PaymentCompleted
                                    setCurrentPage={setCurrentPage}
                                    setSidebarVisibility={setSidebarVisibility} />} />
                                <Route path="/Login" element={<Login
                                    setCurrentPage={setCurrentPage}
                                    setSidebarVisibility={setSidebarVisibility}
                                    loginSuccess={loginSuccess}
                                />} />
                                <Route path="/YourAccount" element={<YourAccount setCurrentPage={setCurrentPage} setSidebarVisibility={setSidebarVisibility} logout={logout} />} />
                                <Route path="/ChangePassword" element={<ChangePassword setCurrentPage={setCurrentPage} setSidebarVisibility={setSidebarVisibility} />} />
                                <Route path="/Register" element={<Register setCurrentPage={setCurrentPage} setSidebarVisibility={setSidebarVisibility} />} />
                                <Route path="/ForgotPassword" element={<ForgotPassword setCurrentPage={setCurrentPage} setSidebarVisibility={setSidebarVisibility} />} />
                                <Route path="/IncompleteQuote" element={<IncompleteQuote setCurrentPage={setCurrentPage} setSidebarVisibility={setSidebarVisibility} />} />
                                <Route path="/MTA" element={<MTA setCurrentPage={setCurrentPage} setSidebarVisibility={setSidebarVisibility} />} />
                                <Route path="/YourDocuments" element={<YourDocuments setCurrentPage={setCurrentPage} setSidebarVisibility={setSidebarVisibility} logout={logout} />} />
                                <Route path="/YourRenewal" element={<YourRenewal setCurrentPage={setCurrentPage} setSidebarVisibility={setSidebarVisibility} />} />
                                <Route path="/RenewalPayment" element={<RenewalPayment setCurrentPage={setCurrentPage} setSidebarVisibility={setSidebarVisibility} />} />
                            </Routes>
                        </span>
                        {(showSideBar) &&
                            <Sidebar
                                page={page}
                                webReference={customerDetails.webReference}
                            />
                        }
                    </div>
                </ErrorBoundary>
                {page !== 5 &&
                    <Footer />
                }
            </>}
        </>
    );
}

export default App